
import { useState, useEffect } from 'react';
import { RouteComponentProps, withRouter, useHistory } from 'react-router-dom';
import { isEmpty } from 'lodash';

import { connect } from 'react-redux';
import { injectIntl, WrappedComponentProps } from 'react-intl';
import { Modal, Tabs, Select } from 'antd';

import { ILoginState, ICurrencyState, getAllCurrencies, loginActions, IHotelsState, getHotels } from '@share/store/slices';
import { getAccountLocale, getFlag, Locales, LocalesCountry } from '@i18n';
import { getSelectedCurrency, RootState, isDetailFromPath, setLocalCurrency, removeChangedCurrency } from '@share/utils';
import { IAccount } from '@share/common-types';
import { SELECTED_LANGUAGE_COUNRTY_LABEL } from '@share/constants';

import { Languages } from './languages';
import { MultiCurrency } from './multi-currency';

import Spain from '@share/assets/images/flags/spain-flag-icon.png';
import US from '@share/assets/images/flags/us-flag-icon.png';
import Italy from '@share/assets/images/flags/italy-flag-icon.png';
import French from '@share/assets/images/flags/france-flag-icon.png';
import Portuguese from '@share/assets/images/flags/brazil-flag-icon.png';

import './style.scss';

interface IMapStateToProps {
  loginStore: ILoginState;
  currencyStore: ICurrencyState;
  hotelsStore: IHotelsState;
}

interface IMapDispatchToProps {
  getAllCurrencies: () => void;
  setUserLanguage: (locale: string) => void;
  getHotels: () => void;
}

interface IProps extends IMapStateToProps, IMapDispatchToProps, WrappedComponentProps, RouteComponentProps {
  forceWhite?: boolean;
  isDrawer?: boolean;

  onOpen?: () => void;
}

function ModalInternalizationComponent(props: IProps) {
  const [show, setShow] = useState(false);
  const [selectedKey, setSelectedKey] = useState('2');
  const history = useHistory();

  const selectedCurrency = getSelectedCurrency(props.loginStore.account as IAccount);
  const locale = getAccountLocale(props?.loginStore?.account as IAccount);

  const { forceWhite, isDrawer, loginStore, currencyStore, hotelsStore, getAllCurrencies, setUserLanguage, getHotels } = props;
  const { account } = loginStore;
  // TODO: Remove this temporary override once the flag is set in account preferences
  // For testing: force enable simple dropdowns
  const useSimpleDropdowns = account?.useSimpleCurrencyLanguageDropdowns || true; // Temporarily force to true for testing

  // Load currencies if using simple dropdowns
  useEffect(() => {
    if (useSimpleDropdowns && !currencyStore.currencies.length) {
      getAllCurrencies();
    }
  }, [useSimpleDropdowns, currencyStore.currencies.length, getAllCurrencies]);

  const getHeaderFontColorStyle = () => {
    const styleBackground: any = {};

    const headerFontsColorDB = account?.headerFontColor;
    if (!isDrawer && !isEmpty(headerFontsColorDB)) {
      styleBackground.color = headerFontsColorDB;
    }
    return styleBackground;
  }

  const getHeaderBackgroundColorStyle = () => {
    const styleBackground: any = {};

    if (isDrawer) {
      styleBackground.backgroundColor = '#000000';
    } else if (forceWhite) {
      styleBackground.backgroundColor = '#FFFFFF';
    } else {
      const headerFontsColorDB = account?.headerFontColor;
      if (!isEmpty(headerFontsColorDB)) {
        styleBackground.backgroundColor = headerFontsColorDB;
      }
    }
    return styleBackground;
  }

  const onSelectOption = (option: string) => {
    setShow(true);
    setSelectedKey(option);

    if (props.onOpen) {
      props.onOpen();
    }
  }

  // Language options for dropdown
  const languageOptions = [
    { value: Locales.English, label: props.intl.formatMessage({ id: 'languages.english' }), flag: US, country: LocalesCountry.EnglishUS },
    { value: Locales.Spanish, label: props.intl.formatMessage({ id: 'languages.spanish' }), flag: Spain, country: LocalesCountry.Spanish },
    { value: Locales.French, label: props.intl.formatMessage({ id: 'languages.french' }), flag: French, country: LocalesCountry.FrenchFR },
    { value: Locales.Italian, label: props.intl.formatMessage({ id: 'languages.italian' }), flag: Italy, country: LocalesCountry.Italy },
    { value: Locales.Portuguese, label: props.intl.formatMessage({ id: 'languages.portuguese' }), flag: Portuguese, country: LocalesCountry.PortugueseBR },
  ];

  const handleLanguageChange = (value: string) => {
    const selectedLang = languageOptions.find(lang => lang.value === value);
    if (selectedLang) {
      localStorage.setItem(SELECTED_LANGUAGE_COUNRTY_LABEL, selectedLang.country);
      setUserLanguage(value);
      window.location.reload();
    }
  };

  const handleCurrencyChange = (value: string) => {
    setLocalCurrency(value);
    
    // Reload or refresh hotels if needed
    if (isDetailFromPath(history)) {
      history.go(0);
    } else {
      removeChangedCurrency();
      if (!hotelsStore.isSearch) {
        history.go(0);
      } else {
        getHotels();
      }
    }
  };

  if (account?.disableLanguages && account?.disableCurrencies) {
    return null;
  }

  // Render simple dropdowns if flag is enabled
  if (useSimpleDropdowns) {
    const currentLanguage = locale || Locales.English;
    const currentLanguageOption = languageOptions.find(lang => lang.value === currentLanguage) || languageOptions[0];

    return (
      <div className="internalization-container internalization-container--dropdowns">
        {!account?.disableLanguages ? (
          <Select
            value={currentLanguage}
            onChange={handleLanguageChange}
            className="internalization-dropdown internalization-dropdown--language"
            // The selector box is always white, so it keeps the default text color instead of the account header font color
            style={{
              '--flag-url': `url(${currentLanguageOption.flag})`
            } as React.CSSProperties}
            dropdownStyle={{ zIndex: 1061 }}
            getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
          >
            {languageOptions.map((lang) => (
              <Select.Option key={lang.value} value={lang.value}>
                <div className="internalization-dropdown__option">
                  <div 
                    className="internalization-dropdown__option-flag"
                    style={{ backgroundImage: `url(${lang.flag})` }}
                  />
                  <span>{lang.label}</span>
                </div>
              </Select.Option>
            ))}
          </Select>
        ) : null}
        
        {(!account?.disableLanguages && !account?.disableCurrencies) ? (
          <i className="internalization-container__divider" style={getHeaderBackgroundColorStyle()}></i>
        ) : null}

        {!account?.disableCurrencies ? (
          <Select
            value={selectedCurrency}
            onChange={handleCurrencyChange}
            className="internalization-dropdown internalization-dropdown--currency"
            dropdownStyle={{ zIndex: 1061 }}
            getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
            loading={!currencyStore.currencies.length}
          >
            {currencyStore.currencies.map((currency: any) => (
              <Select.Option key={currency.code} value={currency.code}>
                {`${currency.code} - ${currency.symbol}`}
              </Select.Option>
            ))}
          </Select>
        ) : null}
      </div>
    );
  }

  return (
    <>
      <div className="internalization-container">
        {!account?.disableLanguages ? (
          <div 
            className={`modal-internalization__languages`}
            style={{ backgroundImage: `url(${getFlag(locale, true)})` }}
            onClick={() => onSelectOption('1')}
          ></div>) : null}
        {(!account?.disableLanguages && !account?.disableCurrencies) ? (<i className="internalization-container__divider" style={getHeaderBackgroundColorStyle()}></i>) : null }

        {!account?.disableCurrencies ? (<div className={`selected_currency`} style={getHeaderFontColorStyle()} onClick={() => onSelectOption('2')}>{selectedCurrency}</div>) : null}
      </div>


      <Modal
        title=""
        centered
        open={show}
        wrapClassName="modal-internalization__wrapper"
        className="modal-internalization"
        onOk={() => setShow(false)}
        onCancel={() => setShow(false)}
        footer={null}
      >
        <Tabs activeKey={selectedKey} onChange={activeKey => setSelectedKey(activeKey)}>
          {!account?.disableLanguages ? (
            <Tabs.TabPane tab="Language" key="1">
              <Languages onLanguageSelected={() => setShow(false)} />
            </Tabs.TabPane>) : null}
          {!account?.disableCurrencies ? (
            <Tabs.TabPane tab="Currency" key="2">
              <MultiCurrency onCurrencySelected={() => setShow(false)} /> 
            </Tabs.TabPane>) : null}
        </Tabs>
      </Modal>
    </>
  );
};

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    loginStore: state.loginStore,
    currencyStore: state.currencyStore,
    hotelsStore: state.hotelsStore,
  };
};

const mapDispatchToProps = (dispatch: any): IMapDispatchToProps => ({
  getAllCurrencies: () => {
    dispatch(getAllCurrencies());
  },
  setUserLanguage: (locale: string) => {
    dispatch(loginActions.setUserLanguage(locale));
  },
  getHotels: () => {
    dispatch(getHotels());
  },
});

export const ModalInternalization = connect(mapStateToProps, mapDispatchToProps)(withRouter(injectIntl(ModalInternalizationComponent)));
